import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Box, Paper, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isValidToken, setIsValidToken] = useState(false);
    const [isTokenVerifying, setIsTokenVerifying] = useState(true);
    const { token } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const verifyToken = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/verify-reset-token/${token}`);

                if (response.data.status === 'success') {
                    setIsValidToken(true);
                } else {
                    setError('This password reset link is invalid or has expired.');
                }
            } catch (error) {
                setError('This password reset link is invalid or has expired.');
            } finally {
                setIsTokenVerifying(false);
            }
        };

        if (token) {
            verifyToken();
        }
    }, [token]);

    const validatePassword = (password) => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) {
            return 'Password must be at least 8 characters long';
        }
        if (!hasUpperCase) {
            return 'Password must contain at least one uppercase letter';
        }
        if (!hasLowerCase) {
            return 'Password must contain at least one lowercase letter';
        }
        if (!hasNumbers) {
            return 'Password must contain at least one number';
        }
        if (!hasSpecialChar) {
            return 'Password must contain at least one special character';
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        // Validate password
        const passwordError = validatePassword(password);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/reset-password/${token}`, { password });

            if (response.data.status === 'success') {
                setSuccessMessage('Password reset successful!');
                setTimeout(() => {
                    navigate('/login', {
                        state: { message: 'Password reset successful! Please log in with your new password.' },
                    });
                }, 2000);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (isTokenVerifying) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isValidToken) {
        return (
            <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Invalid Reset Link
                    </Typography>
                    <Typography align="center">This password reset link is invalid or has expired. Please request a new one.</Typography>
                    <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
                        Back to Login
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Reset Password
                </Typography>
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMessage}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        type="password"
                        label="New Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        margin="normal"
                        required
                        disabled={loading}
                    />
                    <TextField
                        fullWidth
                        type="password"
                        label="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        margin="normal"
                        required
                        disabled={loading}
                    />
                    <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt: 3 }}>
                        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default ResetPassword;
